/**
 * PWA Install Button Component
 * 
 * Industry Standard Design Pattern:
 * - Non-intrusive button in header (like Twitter, Spotify)
 * - Shows only when installable
 * - Hides when installed
 * - Proper error handling
 * - Accessible (keyboard, screen readers)
 * - Responsive (mobile + desktop)
 */

import React, { useState } from 'react';
import { Download, X, Check } from 'lucide-react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import Tooltip from './Tooltip';

import { logger, getErrorMessage } from '../../utils/logger';
interface PWAInstallButtonProps {
  variant?: 'header' | 'card';
  className?: string;
}

export const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({ 
  variant = 'header',
  className = ''
}) => {
  const { showInstallButton, install, dismissInstallPrompt, isInstalled } = usePWAInstall();
  const [isInstalling, setIsInstalling] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Don't render if not installable or already installed
  if (!showInstallButton || isInstalled) {
    return null;
  }

  const handleInstall = async () => {
    setIsInstalling(true);
    
    try {
      await install();
      
      // Show success feedback briefly
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 2000);
      
    } catch (error) {
      logger.error('[PWA Install Button] Installation failed:', { error: getErrorMessage(error) });
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismissInstallPrompt();
  };

  // Header variant (compact, icon-based)
  if (variant === 'header') {
    return (
      <Tooltip content="Install App">
        <button
          onClick={handleInstall}
          disabled={isInstalling}
          aria-label="Install application"
          className={`
            relative
            flex items-center justify-center
            p-2
            text-gray-600 dark:text-slate-300
            hover:bg-gray-100 dark:hover:bg-slate-700
            rounded-lg
            transition-all
            disabled:opacity-50 disabled:cursor-not-allowed
            ${showSuccess ? 'bg-green-50 dark:bg-green-900/20' : ''}
            ${className}
          `}
        >
          {showSuccess ? (
            <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
          ) : (
            <Download className={`w-5 h-5 ${isInstalling ? 'animate-bounce' : ''}`} />
          )}
          
          {/* Small indicator dot */}
          <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
        </button>
      </Tooltip>
    );
  }

  // Card variant (detailed, with text)
  return (
    <div className={`
      relative
      bg-gradient-to-r from-indigo-50 to-blue-50 
      dark:from-indigo-900/20 dark:to-blue-900/20
      border-2 border-indigo-200 dark:border-indigo-800
      rounded-xl
      p-4
      ${className}
    `}>
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        aria-label="Dismiss installation prompt"
        className="
          absolute top-2 right-2
          p-1
          text-gray-400 hover:text-gray-600
          dark:text-slate-500 dark:hover:text-slate-300
          rounded
          hover:bg-white/50 dark:hover:bg-slate-800/50
          transition
        "
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-4">
        <div className="
          flex-shrink-0
          w-12 h-12
          bg-gradient-to-br from-indigo-600 to-blue-600
          dark:from-indigo-500 dark:to-blue-500
          rounded-xl
          flex items-center justify-center
          shadow-lg
        ">
          <Download className="w-6 h-6 text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 mb-1">
            Install App
          </h3>
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-3">
            Install QA Agent for quick access, offline support, and a native app experience.
          </p>

          <button
            onClick={handleInstall}
            disabled={isInstalling}
            className="
              inline-flex items-center gap-2
              px-4 py-2
              bg-indigo-600 hover:bg-indigo-700
              dark:bg-indigo-500 dark:hover:bg-indigo-600
              text-white
              text-sm font-semibold
              rounded-lg
              shadow-md hover:shadow-lg
              transition-all
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {isInstalling ? (
              <>
                <Download className="w-4 h-4 animate-bounce" />
                Installing...
              </>
            ) : showSuccess ? (
              <>
                <Check className="w-4 h-4" />
                Installed!
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Install Now
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallButton;

